import React from 'react';
import { useNavigate } from 'react-router-dom';
import './SmartStopLoss.css';

const GapUpGuide: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="article-page">
      {/* Back Navigation */}
      <div className="article-nav">
        <button className="back-button" onClick={() => navigate('/learning-hub')}>
          ← Back to Learning Hub
        </button>
      </div>

      {/* Article Header */}
      <header className="article-header">
        <div className="article-badge">MOMENTUM STRATEGY</div>
        <h1 className="article-title">How to Trade Positive Gap Up Screener</h1>
        <div className="article-meta">
          <span className="meta-item">📅 Updated December 2024</span>
          <span className="meta-item">⏱️ 7 min read</span>
          <span className="meta-item">🚀 High Momentum Setup</span>
        </div>
      </header>

      {/* Article Content */}
      <article className="article-content">

        {/* Introduction */}
        <section className="content-section">
          <h2>What is Gap Up Trading?</h2>
          <div className="ai-badge-container">
            <span className="ai-badge">📊 Pattern Detected by Screener</span>
            <span className="market-badge">⚡ Best for Small & Mid Cap</span>
          </div>
          <p className="lead-text">
            A gap up occurs when a stock opens significantly higher than its previous day's closing price, creating
            a visible "gap" on the chart. This powerful momentum signal indicates strong buying interest and positive
            sentiment, often driven by overnight news, earnings beats, or market catalysts.
          </p>

          <p>
            <strong>What is a Gap?</strong> A gap is a change in price levels between the close and open of two
            consecutive trading days. When today's opening price is higher than yesterday's close, it creates a
            positive gap up.
          </p>

          <div className="highlight-box">
            <strong>Why Gap Ups Matter:</strong>
            <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
              <li>Signal of strong overnight demand and bullish sentiment</li>
              <li>Often driven by positive catalysts (earnings, news, upgrades)</li>
              <li>Create immediate profit for overnight holders</li>
              <li>Can lead to continued momentum throughout the day</li>
              <li>High-probability setups when traded correctly</li>
            </ul>
          </div>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">📈</div>
              <p className="placeholder-text">Gap Up Pattern Illustration</p>
              <p className="placeholder-desc">Shows price gap between previous close and current open with continuation move</p>
            </div>
          </div>
        </section>

        {/* Types of Gaps */}
        <section className="content-section">
          <h2>Types of Gap Ups</h2>

          <h3>1. Breakaway Gap (Most Powerful)</h3>
          <div className="method-card">
            <p>Gap that occurs at the start of a new trend:</p>
            <ul>
              <li>Breaks out of consolidation or base pattern</li>
              <li>Usually accompanied by <strong>very high volume</strong></li>
              <li>Often signals major trend change</li>
              <li>Rarely fills - gap becomes support</li>
              <li><strong>Best for swing trading</strong></li>
            </ul>

            <div className="example-box">
              <h4>Breakaway Gap Example:</h4>
              <p>Stock consolidates between ₹450-₹480 for 3 weeks</p>
              <p>Gaps up to ₹495 on earnings beat (3x volume)</p>
              <p>Continues to ₹540 over next week</p>
              <p className="result">✅ Classic breakaway gap - rarely fills!</p>
            </div>
          </div>

          <h3>2. Continuation Gap (Runaway Gap)</h3>
          <div className="method-card">
            <p>Gap that occurs in the middle of an existing strong trend:</p>
            <ul>
              <li>Happens during strong momentum moves</li>
              <li>Confirms trend strength and continuation</li>
              <li>Volume usually above average</li>
              <li>Often doesn't fill during the move</li>
              <li>Good for adding to existing positions</li>
            </ul>
          </div>

          <h3>3. Exhaustion Gap (Warning Sign)</h3>
          <div className="method-card">
            <p>Gap that occurs near the end of a trend:</p>
            <ul>
              <li>Final surge after extended rally</li>
              <li>Often followed by reversal or consolidation</li>
              <li>Volume may be high but buying fades quickly</li>
              <li>Tends to fill within days</li>
              <li><strong>Avoid trading these - high risk!</strong></li>
            </ul>

            <div className="warning-box">
              <strong>⚠️ How to Spot Exhaustion Gaps:</strong> If a stock has already rallied 30-50% and gaps up
              again on climactic volume, it's likely an exhaustion gap. These are traps for late buyers - avoid them!
            </div>
          </div>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">📊</div>
              <p className="placeholder-text">Three Types of Gaps Comparison</p>
              <p className="placeholder-desc">Visual comparison of breakaway, continuation, and exhaustion gaps</p>
            </div>
          </div>
        </section>

        {/* Gap and Go Strategy */}
        <section className="content-section">
          <h2>Gap and Go Trading Strategy</h2>

          <h3>What is Gap and Go?</h3>
          <div className="method-card">
            <p>
              The Gap and Go strategy involves buying stocks that gap up strongly at the open and continue moving
              higher throughout the day. This works when there's genuine buying pressure, not just a gap followed
              by immediate selling.
            </p>

            <div className="highlight-box">
              <strong>Ideal Gap and Go Criteria:</strong>
              <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
                <li><strong>Gap Size:</strong> Minimum 4% gap (prefer 5-10% for best setups)</li>
                <li><strong>Volume:</strong> At least 2x average volume (3x+ is ideal)</li>
                <li><strong>Catalyst:</strong> Clear reason for the gap (earnings, news, upgrade)</li>
                <li><strong>Pre-market Action:</strong> Strong pre-market buying and volume</li>
                <li><strong>Market Context:</strong> Broader market is bullish</li>
              </ul>
            </div>
          </div>

          <h3>Entry Strategies</h3>
          <div className="trailing-rules">
            <div className="rule-card">
              <div className="rule-number">Strategy 1</div>
              <h4>Wait for First Hour Confirmation</h4>
              <p>The basic tenet of gap trading - allow one hour after market opens:</p>
              <ul>
                <li>Let stock establish its opening range (first hour)</li>
                <li>Watch for price to hold above gap-up level</li>
                <li>Enter when price breaks above first hour high</li>
                <li>Provides better confirmation, less whipsaw</li>
              </ul>

              <div className="example-calc">
                <p><strong>Previous Close:</strong> ₹500</p>
                <p><strong>Gap Open:</strong> ₹525 (5% gap)</p>
                <p><strong>First Hour Range:</strong> ₹523-₹532</p>
                <p><strong>Entry:</strong> Buy at ₹533 (breaks first hour high)</p>
              </div>
            </div>

            <div className="rule-card">
              <div className="rule-number">Strategy 2</div>
              <h4>Immediate Entry (Aggressive)</h4>
              <p>Enter at the open if all criteria are perfect:</p>
              <ul>
                <li>Strong gap (7%+) with clear catalyst</li>
                <li>Very high pre-market volume (3x+)</li>
                <li>Stock breaking out of base pattern</li>
                <li>Best for experienced traders only</li>
              </ul>
            </div>

            <div className="rule-card">
              <div className="rule-number">Strategy 3</div>
              <h4>Pullback Entry (Conservative)</h4>
              <p>Wait for gap to be tested before entering:</p>
              <ul>
                <li>After gap up, wait for minor pullback</li>
                <li>Enter when price bounces off gap level (now support)</li>
                <li>Provides tighter stop and better risk/reward</li>
                <li>Not all gaps will pull back for entry</li>
              </ul>

              <div className="tip-box">
                <strong>💡 Pro Tip:</strong> If the stock "holds the gap" (doesn't fill the gap down), it's a very
                bullish sign. The gap becomes support, and you can enter on the bounce off that level.
              </div>
            </div>
          </div>
        </section>

        {/* Finding Catalyst */}
        <section className="content-section">
          <h2>Finding the Catalyst Behind Gap Ups</h2>

          <div className="method-card">
            <h4>Catalyst Identification by Our Screener:</h4>
            <p>Our screener automatically identifies the catalyst behind each gap up:</p>
            <ul>
              <li><strong>Earnings Beats:</strong> Company reports better-than-expected earnings</li>
              <li><strong>Positive News:</strong> New contracts, product launches, regulatory approvals</li>
              <li><strong>Analyst Upgrades:</strong> Major brokerage upgrades target price</li>
              <li><strong>Sector Momentum:</strong> Entire sector rallying on positive development</li>
              <li><strong>Market Sympathy:</strong> Similar stocks gapping up on related news</li>
            </ul>

            <div className="warning-box">
              <strong>⚠️ No Catalyst = High Risk:</strong> If there's NO significant catalyst responsible for
              the gap up, be very cautious. Gaps without news often fade and fill. Consider shorting these instead
              of going long.
            </div>
          </div>

          <h3>Volume Confirmation is Critical</h3>
          <div className="method-card">
            <p>Volume validates the gap:</p>
            <ul>
              <li><strong>Minimum:</strong> 2x average daily volume</li>
              <li><strong>Ideal:</strong> 3x+ average volume</li>
              <li><strong>Pre-market volume:</strong> Should also be significantly elevated</li>
              <li>High volume = institutional participation = higher success rate</li>
            </ul>

            <div className="highlight-box">
              <strong>Remember:</strong> Gaps with high volume and clear catalysts have 60-70% chance of continuing
              higher. Gaps without volume or catalysts often fail and fill within hours.
            </div>
          </div>
        </section>

        {/* Profit Targets */}
        <section className="content-section">
          <h2>Profit Targets and Exit Strategy</h2>

          <h3>Target Setting Methods</h3>
          <div className="method-card">
            <h4>Method 1: Intraday Targets</h4>
            <p>For day trading gap ups:</p>
            <ul>
              <li>Target 1.5-2x the gap size above entry</li>
              <li>Exit by end of day (don't hold overnight)</li>
              <li>Take profits into strength</li>
            </ul>
          </div>

          <div className="method-card">
            <h4>Method 2: Swing Trading Targets</h4>
            <p>For holding multi-day:</p>
            <ul>
              <li>Use resistance levels as targets</li>
              <li>Target 10-20% gains from gap point</li>
              <li>Trail stops as position moves favorably</li>
              <li>Watch for distribution or selling pressure</li>
            </ul>
          </div>

          <div className="method-card">
            <h4>Method 3: Trailing Stop Approach</h4>
            <p>Let winners run:</p>
            <ul>
              <li>Move stop to breakeven quickly</li>
              <li>Trail stop below each day's low</li>
              <li>Exit when momentum clearly fades</li>
              <li>Captures extended moves (20-50%+)</li>
            </ul>
          </div>

          <div className="highlight-box">
            <strong>Scaling Out Strategy:</strong> Sell 1/3 at quick 5-7% profit, 1/3 at 10-15%, and trail final
            1/3 for bigger moves. This balances profit-taking with staying in for potential huge gains.
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="content-section">
          <h2>Common Mistakes to Avoid</h2>

          <div className="mistake-card">
            <h4>❌ Mistake #1: Chasing the Gap</h4>
            <p>
              Buying too late, far above the gap level, creates poor risk/reward and increases odds of reversal.
              <strong> Wait for proper entry setup - first hour high break or pullback to gap.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #2: Ignoring the Catalyst</h4>
            <p>
              Trading gaps without understanding WHY they're gapping often leads to losses.
              <strong> Always find the catalyst - no news = high risk of gap fill.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #3: Trading Low Volume Gaps</h4>
            <p>
              Gaps without volume confirmation (2x+) frequently fail and reverse.
              <strong> Require minimum 2x average volume, prefer 3x+.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #4: Holding Gap Fills</h4>
            <p>
              When a gap fills (price returns to previous close), the pattern has failed. Holding hoping for recovery leads to bigger losses.
              <strong> Exit immediately if gap fills - don't hold and hope.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #5: Trading Exhaustion Gaps</h4>
            <p>
              Buying gaps after extended rallies (30-50%+) often catches the top.
              <strong> Avoid climactic gaps - they're exhaustion signals, not continuation.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #6: No Position Sizing Plan</h4>
            <p>
              Gap trades are volatile - over-risking can lead to catastrophic losses.
              <strong> Never risk more than 1-2% of capital on any single gap trade.</strong>
            </p>
          </div>
        </section>

        {/* Real Example */}
        <section className="content-section">
          <h2>Real Gap Up Trading Example</h2>

          <div className="method-card">
            <h4>Successful Gap and Go Trade:</h4>
            <ul>
              <li><strong>Stock:</strong> INFY</li>
              <li><strong>Previous Close:</strong> ₹1,450</li>
              <li><strong>Catalyst:</strong> Quarterly earnings beat expectations + raised guidance</li>
              <li><strong>Gap Open:</strong> ₹1,525 (5.2% gap) with 3.5x volume</li>
              <li><strong>Pre-market:</strong> Strong buying from ₹1,510 to ₹1,525</li>
              <li><strong>First Hour Range:</strong> ₹1,520-₹1,538</li>
              <li><strong>Entry:</strong> ₹1,540 (breaks above first hour high)</li>
              <li><strong>Gap Size:</strong> 5.2% with strong volume support</li>
              <li><strong>Target:</strong> ₹1,595 (swing trading approach)</li>
              <li><strong>Result:</strong> Hit ₹1,595 by day 3 (3.6% gain)</li>
            </ul>

            <div className="highlight-box">
              <strong>Why This Trade Worked:</strong>
              <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
                <li>✅ Strong fundamental catalyst (earnings beat)</li>
                <li>✅ Large gap (5.2%) with very high volume (3.5x)</li>
                <li>✅ Stock held the gap and didn't fill back down</li>
                <li>✅ Entry after first hour confirmation (not chasing)</li>
                <li>✅ Tight stop below gap kept risk minimal (1.4%)</li>
                <li>✅ Clear profit target based on risk/reward</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Quick Reference */}
        <section className="content-section">
          <h2>Gap Up Trading Quick Reference</h2>

          <div className="reference-table">
            <table>
              <thead>
                <tr>
                  <th>Gap Type</th>
                  <th>Characteristics</th>
                  <th>Trading Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Breakaway Gap</td>
                  <td>Start of new trend, very high volume</td>
                  <td>Buy and hold - best setup</td>
                </tr>
                <tr>
                  <td>Continuation Gap</td>
                  <td>Mid-trend, confirms strength</td>
                  <td>Add to position or new entry</td>
                </tr>
                <tr>
                  <td>Exhaustion Gap</td>
                  <td>After big rally, climactic</td>
                  <td>Avoid or consider shorting</td>
                </tr>
                <tr>
                  <td>Gap Size</td>
                  <td>4-10% ideal</td>
                  <td>Bigger gaps = more momentum</td>
                </tr>
                <tr>
                  <td>Volume</td>
                  <td>2-3x average minimum</td>
                  <td>Higher = better confirmation</td>
                </tr>
                <tr>
                  <td>Best Entry</td>
                  <td>After first hour confirmation</td>
                  <td>Reduces whipsaw risk</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Summary */}
        <section className="content-section summary-section">
          <h2>Key Takeaways</h2>
          <ul className="takeaway-list">
            <li>✅ Gap up = stock opens higher than previous close, signaling strong buying</li>
            <li>✅ Breakaway gaps (start of trend) are best - rarely fill, high success rate</li>
            <li>✅ Always identify the catalyst - no news = high risk of gap fill</li>
            <li>✅ Volume confirmation (2-3x average) is critical for success</li>
            <li>✅ Wait for first hour to establish range before entering (reduces whipsaw)</li>
            <li>✅ Place stop below gap level - if gap fills, exit immediately</li>
            <li>✅ Ideal gap size is 4-10% for best risk/reward</li>
            <li>✅ Avoid exhaustion gaps after extended rallies (30-50%+ moves)</li>
          </ul>

          <div className="final-note">
            <strong>Remember:</strong> Gap up trading is about momentum and follow-through. The most profitable
            gaps have clear catalysts, high volume, and continue moving higher without filling. Our Gap Up Screener
            identifies these setups automatically every morning, filtering for proper volume and catalyst confirmation,
            so you can focus on execution rather than searching.
          </div>
        </section>

      </article>

      {/* Related Articles */}
      <section className="related-articles">
        <h3>Continue Learning</h3>
        <div className="related-grid">
          <div className="related-card" onClick={() => navigate('/learning-hub/ipo-bull-guide')}>
            <div className="related-icon">🚀</div>
            <h4>IPO Bull Run Trading</h4>
            <p>Catch early IPO momentum with proper entry strategies</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub/narrow-range-guide')}>
            <div className="related-icon">📏</div>
            <h4>Narrow Range Patterns</h4>
            <p>Trade compression and expansion setups</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub')}>
            <div className="related-icon">📚</div>
            <h4>View All Guides</h4>
            <p>Explore all swing trading tutorials</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="article-cta">
        <h3>Ready to Find Gap Up Opportunities?</h3>
        <p>Use our screener to find stocks gapping up with strong catalysts and volume</p>
        <button className="cta-button" onClick={() => navigate('/screener/postive-gap-up')}>
          Try Gap Up Screener →
        </button>
      </section>

    </div>
  );
};

export default GapUpGuide;
